import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import CrudApi from '@src/API/CrudApi'
import { showLoadingToast, showSuccessToast, showErrorToast } from '@components/Notify/Toast'
import { EMAIL_DEFAULT_PAGE_SIZE } from '../helpers/emailConstants'
import { decodeMailHeaders } from '../utils/emailHelpers'

// Stage-1 loading toast duration. Kept short so it doesn't linger; finite so
// react-hot-toast auto-dismisses it (a loading toast only hangs forever when its
// duration is Infinity). The stage-2 success/error toast arrives separately from
// the realtime socket.
const LOADING_TOAST_MS = 1800

// Manual (non-silent) seen/star actions register their message uids here so the
// realtime listener shows a success toast for them — while staying silent for the
// automatic mark-as-read fired when a mail is opened. Matched on uid because the
// 'webmail.operation.status' notification echoes back the same message_ids.
const pendingSeenStarUids = new Set()

const registerPendingSeenStar = (uids = []) => {
  (uids || []).forEach((uid) => pendingSeenStarUids.add(String(uid)))
  // Safety cleanup if the completion event never arrives.
  setTimeout(() => (uids || []).forEach((uid) => pendingSeenStarUids.delete(String(uid))), 15000)
}

export const consumePendingSeenStar = (uids = []) => {
  let matched = false
  ;(uids || []).forEach((uid) => { if (pendingSeenStarUids.delete(String(uid))) matched = true })
  return matched
}

export const getMails = createAsyncThunk(
  'emails/getMails',
  async (params, { fulfillWithValue, rejectWithValue }) => {
  // For first page, load only headers to speed up folder switching
  // Full content will be loaded when user opens an email
  const apiParams = {
    ...params,
    headersOnly: params?.page === 1 && params?.headersOnly !== false
  };
  
  const response = await CrudApi({ apiParams: apiParams }).get('/api/web-mail/messages')
  .then(res => res)
  .catch(e => e)

  if (response?.status === 200) {
    return fulfillWithValue({
      type: params?.type,
      data: (response?.data?.data || []).map(decodeMailHeaders),
      unseen_counts: response?.data?.unseen_counts || 0,
      total: response?.data?.total || 0,
      contacts: response?.data?.contacts || [],
      signature: response?.data?.signature || ''
    });
  }
  return rejectWithValue(response?.data);
})

// Seen/star are optimistic: the reducer flips the flag immediately (see
// extraReducers .pending). The backend queues the IMAP change and returns fast;
// the realtime 'webmail.operation.status' event reconciles it. On an HTTP-level
// failure we refetch the folder to restore the truth.
export const changeSeenStatus = createAsyncThunk(
  'emails/updateStatus',
  async ({messages_ids, mark_as_read , activeTab, silent = false}, { fulfillWithValue, rejectWithValue , dispatch}) => {
  // `silent` skips the toast for the auto mark-as-read fired when opening a mail.
  if (!silent) {
    showLoadingToast(mark_as_read ? 'Marking as read...' : 'Marking as unread...', { duration: LOADING_TOAST_MS })
    registerPendingSeenStar(messages_ids)
  }
  const response = await CrudApi().post('/api/web-mail/change-seen-status', { messages_ids, mark_as_read , folder: activeTab})
  .then(res => res)
  .catch(e => e)

  if (response?.status === 200) {
    return fulfillWithValue(response?.data);
  }
  dispatch(getMails({ type: activeTab }))
  if (!silent) showErrorToast('Action failed, changes were reverted')
  return rejectWithValue(response?.data);
})

export const changeStarStatus = createAsyncThunk(
  'emails/changeStarStatus',
  async ({messages_ids, mark_as_starred , activeTab, silent = false}, { fulfillWithValue, rejectWithValue , dispatch}) => {
  if (!silent) {
    showLoadingToast(mark_as_starred ? 'Adding star...' : 'Removing star...', { duration: LOADING_TOAST_MS })
    registerPendingSeenStar(messages_ids)
  }
  const response = await CrudApi().post('/api/web-mail/change-star-status', { messages_ids, mark_as_starred , folder: activeTab})
  .then(res => res)
  .catch(e => e)

  if (response?.status === 200) {
    return fulfillWithValue(response?.data);
  }
  dispatch(getMails({ type: activeTab }))
  if (!silent) showErrorToast('Action failed, changes were reverted')
  return rejectWithValue(response?.data);
})

// Move/delete/send/draft are queued on the backend. The list refresh is driven
// by the realtime 'webmail.*.status' event when the IMAP work actually finishes
// (RealtimeNotificationsListener) — NOT by this immediate response — so we never fetch
// stale (pre-completion) data and never double-fetch.
export const moveToFolder = createAsyncThunk(
  'emails/moveToFolder',
  async ({messages_ids, activeTab, target}, { fulfillWithValue, rejectWithValue }) => {
  const isArchive = target === 'Archive';
  showLoadingToast(isArchive ? 'Archiving...' : 'Moving...', { duration: LOADING_TOAST_MS })
  const response = await CrudApi().post('/api/web-mail/move-to-folder', { messages_ids, folder: activeTab, target})
  .then(res => res)
  .catch(e => e)

  if (response?.status === 200) {
    return fulfillWithValue(response?.data);
  }
  showErrorToast(isArchive ? 'Failed to archive' : 'Failed to move')
  return rejectWithValue(response?.data);
})

export const deleteMessage = createAsyncThunk(
  'emails/deleteMessage',
  async ({messages_ids, activeTab}, { fulfillWithValue, rejectWithValue }) => {
  showLoadingToast('Deleting...', { duration: LOADING_TOAST_MS })
  const response = await CrudApi().post('/api/web-mail/delete-message', { messages_ids, folder: activeTab})
  .then(res => res)
  .catch(e => e)

  if (response?.status === 200) {
    return fulfillWithValue(response?.data);
  }
  showErrorToast('Failed to delete')
  return rejectWithValue(response?.data);
})

// Send/save-draft: refresh comes from the realtime 'sent'/'saved' event (Sent /
// Drafts folder) on completion — not from this immediate response.
export const sendMessage = createAsyncThunk(
  'emails/sendMessage',
  async (data, { fulfillWithValue, rejectWithValue }) => {
  const payload = { ...(data || {}) }
  delete payload.activeTab // frontend-only routing field; not part of the message
  const subjectSuffix = payload?.subject ? `: ${payload.subject}` : ''
  showLoadingToast('Sending email...', { duration: LOADING_TOAST_MS })
  const response = await CrudApi().post('/api/web-mail/send-message', payload)
  .then(res => res)
  .catch(e => e)

  if (response?.status === 200) {
    return fulfillWithValue(response?.data);
  }
  showErrorToast(`Failed to send email${subjectSuffix}`)
  return rejectWithValue(response?.data);
})

export const addMessageToDraft = createAsyncThunk(
  'emails/addMessageToDraft',
  async ({ data }, { fulfillWithValue, rejectWithValue }) => {
  showLoadingToast('Saving draft...', { duration: LOADING_TOAST_MS })
  const response = await CrudApi().post('/api/web-mail/save-draft', data)
  .then(res => res)
  .catch(e => e)

  if (response?.status === 200) {
    return fulfillWithValue(response?.data);
  }
  showErrorToast('Failed to save draft')
  return rejectWithValue(response?.data);
})

export const addToContacts = createAsyncThunk(
  'emails/addToContacts', 
  async ({ email, activeTab , withRefresh = true}, { fulfillWithValue, rejectWithValue , dispatch}) => {
  const response = await CrudApi().post('/api/web-mail/contact-emails/update', { email })
  .then(res => res)
  .catch(e => e)
  .finally(() => {
    if (withRefresh) {
      dispatch(getMails({ type: activeTab }))
    }
  })

  if (response?.status === 200) {
    return fulfillWithValue(response?.data);
  }
  return rejectWithValue(response?.data);
})

export const updateSignature = createAsyncThunk(
  'emails/updateSignature', 
  async (signature, { fulfillWithValue, rejectWithValue}) => {
  const response = await CrudApi().post('/api/web-mail/signature', { signature } )
  .then(res => res)
  .catch(e => e)

  if (response?.status === 200) {
    return fulfillWithValue(response?.data);
  }
  return rejectWithValue(response?.data);
})

export const emailsSlice = createSlice({
  name: 'emails',
  initialState: {
    inbox: [],
    Drafts: [],
    Sent: [],
    Spam: [],
    Trash: [],
    Archive: [],
    unseen_counts: {},
    isLoading: false,
    isFetchingMails: false,
    total: 0,
    contacts: [],
    signature: '',
    currentPage: 1,
    rowsPerPage: EMAIL_DEFAULT_PAGE_SIZE
  },
  reducers: {
    setIsLoading: (state, action) => {
      const { status , message } = action.payload || {}
      state.isLoading = status
      // No toast notifications - spinner will be shown in UI
    },
    setPagination: (state, action) => {
      const { page, pageSize } = action.payload || {}
      if (page !== undefined) state.currentPage = page
      if (pageSize !== undefined) state.rowsPerPage = pageSize
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getMails.pending, (state) => {
        state.isLoading = true
        state.isFetchingMails = true
      })
      .addCase(getMails.fulfilled, (state, action) => {
        const { type, data =[], unseen_counts = {} , total, contacts = [] , signature = '' } = action?.payload || {}
        state.isLoading = false
        state.isFetchingMails = false
        if (type) {
          state[type] = data
        }
          state.unseen_counts = unseen_counts
          state.total = total
          state.contacts = contacts
          state.signature = signature
          // Update pagination state if provided in meta
          if (action.meta?.arg?.page !== undefined) {
            state.currentPage = action.meta.arg.page
          }
          if (action.meta?.arg?.pageSize !== undefined) {
            state.rowsPerPage = action.meta.arg.pageSize
          }
      })
      .addCase(getMails.rejected, (state, action) => {
        state.isLoading = false
        state.isFetchingMails = false
      })
      .addCase(addToContacts.fulfilled, (state, action) => {
        state.contacts = action?.payload?.data || []
      })
      .addCase(updateSignature.fulfilled, (state, action) => {
        showSuccessToast('Signature updated successfully')
        state.signature = action?.payload?.data?.signature
      })
      // Optimistic star: flip the flag immediately in the active folder.
      .addCase(changeStarStatus.pending, (state, action) => {
        const { messages_ids = [], mark_as_starred, activeTab } = action.meta?.arg || {}
        const uids = new Set(messages_ids)
        ;(state[activeTab] || []).forEach(mail => {
          if (uids.has(mail?.uid)) mail.is_starred = mark_as_starred
        })
      })
      // Optimistic seen/unseen: flip the flag immediately in the active folder.
      .addCase(changeSeenStatus.pending, (state, action) => {
        const { messages_ids = [], mark_as_read, activeTab } = action.meta?.arg || {}
        const uids = new Set(messages_ids)
        ;(state[activeTab] || []).forEach(mail => {
          if (uids.has(mail?.uid)) mail.is_seen = mark_as_read
        })
      })
  }
})

export const { setIsLoading, setPagination } = emailsSlice.actions
export default emailsSlice.reducer
